import type { FormEvent } from 'react'
import { ArrowRight } from 'lucide-react'
import { Button, Dialog, Notice, TextField } from '@/components/play'
import { IngestRegionPicker } from '@/components/IngestRegionPicker'
import type { NewEvent } from '@/pages/dashboard/types'

type EventFormProps = {
  form: NewEvent
  setForm: (form: NewEvent) => void
  createEvent: (event: FormEvent) => void
  isCreating: boolean
  error: string | null
}

export function CreateEventForm({
  form,
  setForm,
  createEvent,
  isCreating,
  error,
}: EventFormProps) {
  return (
    <form className="space-y-5" onSubmit={createEvent}>
      <TextField
        label="Event name"
        description="You can add the date, location, and course later."
        placeholder="Your next great race"
        autoFocus
        value={form.name}
        onChange={(event) => setForm({ ...form, name: event.target.value })}
        disabled={isCreating}
        required
        maxLength={255}
      />
      <IngestRegionPicker
        value={form.ingest_region}
        onChange={(ingest_region) => setForm({ ...form, ingest_region })}
        disabled={isCreating}
      />
      {error && (
        <Notice tone="error" alert>
          {error}
        </Notice>
      )}
      <Button
        type="submit"
        className="w-full"
        loading={isCreating}
        disabled={!form.name.trim()}
      >
        Create event
        <ArrowRight aria-hidden="true" />
      </Button>
      <p className="text-sm text-neutral-7">
        Your event gets a public page. Next, add cameras and share their
        invites.
      </p>
    </form>
  )
}

export function CreateEventDialog({
  showNew,
  setShowNew,
  ...props
}: EventFormProps & { showNew: boolean; setShowNew: (open: boolean) => void }) {
  return (
    <Dialog
      title="Create event"
      description="Give your event a name."
      open={showNew}
      onOpenChange={(open) => {
        if (!props.isCreating) setShowNew(open)
      }}
    >
      <CreateEventForm {...props} />
    </Dialog>
  )
}
